import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { MasterDataItem, MasterDataKind, MasterDataTutte, MedCampi, MedScheda, MedStato } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { ErrorBanner, Spinner, formatDay } from '../common'
import { DataTable } from '../DataTable'
import { Finestra } from '../ModificheGitlab'
import { SelettoreAnagrafica } from '../SelettoreAnagrafica'
import { BadgeStatoMed, STATI_MED, classiElettriche, etichettaStatoMed, partiApplicate } from './comuni'
import { BadgeStatoRichiamo } from './Richiami'
import { ProfiloModello } from './Verifiche'

/**
 * The windows of CENSO MED (ADR-0027): the record of a device, the same
 * values on many devices, «not a medical device», a model of the tenant.
 *
 * Every write takes two steps: the form, then what will change («Rivedi»),
 * then the save. Wards and sites come from `/medical/master-data` (the
 * clinical engineering role does not open «Anagrafiche») and a new one is
 * created on the spot.
 */

type Campo = keyof MedCampi

/** The label of each editable field, for the form and for the review. */
export function etichetta(campo: Campo): string {
  const e: Record<Campo, string> = {
    inventory_number: t('N. inventario'),
    serial_number: t('Numero di serie'),
    udi_di: t('UDI-DI'),
    cnd_code: t('Codice CND'),
    civab_code: t('Codice CIVAB'),
    series: t('Serie'),
    model: t('Modello'),
    location_id: t('Sede'),
    org_unit_id: t('Reparto'),
    cost_center_id: t('Centro di costo'),
    room: t('Stanza'),
    bed: t('Letto'),
    status: t('Stato'),
    status_reason: t('Motivo dello stato'),
    responsible_name: t('Responsabile'),
    responsible_email: t('Email del responsabile'),
    acceptance_date: t('Collaudo'),
    install_date: t('Installazione'),
    electrical_class: t('Classe di protezione'),
    applied_part: t('Parte applicata'),
    patient_connected: t('Collegato al paziente'),
    notes: t('Note'),
  }
  return e[campo]
}

/** The record's current values, as the form holds them. */
function valoriDi(d: MedScheda): MedCampi {
  return {
    inventory_number: d.inventory_number ?? '',
    serial_number: d.serial_number ?? '',
    udi_di: d.udi_di ?? '',
    cnd_code: d.cnd_code ?? '',
    civab_code: d.civab_code ?? '',
    series: d.series ?? '',
    model: d.model ?? '',
    location_id: d.location?.id ?? null,
    org_unit_id: d.org_unit?.id ?? null,
    cost_center_id: d.cost_center?.id ?? null,
    room: d.room ?? '',
    bed: d.bed ?? '',
    status: d.status,
    status_reason: d.status_reason ?? '',
    responsible_name: d.responsible_name ?? '',
    responsible_email: d.responsible_email ?? '',
    acceptance_date: d.acceptance_date ? d.acceptance_date.slice(0, 10) : '',
    install_date: d.install_date ? d.install_date.slice(0, 10) : '',
    electrical_class: d.electrical_class ?? '',
    applied_part: d.applied_part ?? '',
    patient_connected: d.patient_connected,
    notes: d.notes ?? '',
  }
}

function norma(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim()
}

/** What a value reads as in the review. */
function leggibile(campo: Campo, v: unknown, anagrafiche: MasterDataTutte | null): string {
  if (norma(v) === '') return '—'
  if (campo === 'status') return etichettaStatoMed(String(v))
  if (campo === 'patient_connected') return v ? t('sì') : t('no')
  if (campo === 'acceptance_date' || campo === 'install_date') return formatDay(String(v))
  const kind = ({ location_id: 'location', org_unit_id: 'org_unit', cost_center_id: 'cost_center' } as Partial<Record<Campo, MasterDataKind>>)[campo]
  if (kind && anagrafiche) return anagrafiche[kind].find((x) => x.id === v)?.name ?? String(v)
  return String(v)
}

function Revisione({ righe }: { righe: { campo: Campo; prima: string; dopo: string }[] }) {
  return (
    <DataTable
      rows={righe}
      rowKey={(r) => r.campo}
      paginazione={false}
      strumenti={false}
      columns={[
        { key: 'campo', header: t('Campo'), value: (r) => etichetta(r.campo) },
        { key: 'prima', header: t('Prima'), value: (r) => r.prima },
        { key: 'dopo', header: t('Dopo'), value: (r) => r.dopo, render: (r) => <strong>{r.dopo}</strong> },
      ]}
    />
  )
}

/** A ward or a site created on the spot, without leaving the form. */
function NuovaVoce({ kind, onCreata }: { kind: 'org_unit' | 'location'; onCreata: (v: MasterDataItem) => void }) {
  const [aperta, setAperta] = useState(false)
  const [nome, setNome] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  if (!aperta) {
    return (
      <button type="button" className="linklike" onClick={() => setAperta(true)}>
        {kind === 'org_unit' ? t('+ Nuovo reparto') : t('+ Nuova sede')}
      </button>
    )
  }
  async function crea() {
    setLavoro(true)
    setErrore(null)
    try {
      onCreata(await api.medMasterDataCreate(kind, { name: nome.trim() }))
      setAperta(false)
      setNome('')
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  return (
    <span className="med-nuova-voce">
      <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={200}
             aria-label={kind === 'org_unit' ? t('Nome del reparto') : t('Nome della sede')}
             placeholder={kind === 'org_unit' ? t('Nome del reparto') : t('Nome della sede')} />
      <button type="button" className="secondary" onClick={() => void crea()} disabled={lavoro || !nome.trim()}>{t('Crea')}</button>
      <button type="button" className="secondary" onClick={() => setAperta(false)} disabled={lavoro}>{t('Annulla')}</button>
      <ErrorBanner error={errore} />
    </span>
  )
}

/** The master data of the area, with a way to add the ones just created. */
function useAnagraficheMed() {
  const dati = useAsync(() => api.medMasterData(), [])
  const [aggiunte, setAggiunte] = useState<MasterDataItem[]>([])
  const tutte: MasterDataTutte | null = dati.data ? {
    cost_center: dati.data.cost_center,
    org_unit: [...dati.data.org_unit, ...aggiunte.filter((v) => v.kind === 'org_unit')],
    location: [...dati.data.location, ...aggiunte.filter((v) => v.kind === 'location')],
  } : null
  return { tutte, errore: dati.error, aggiungi: (v: MasterDataItem) => setAggiunte((a) => [...a, v]) }
}

export function FinestraScheda({ d, onChiudi, onFatto }: {
  d: MedScheda
  onChiudi: () => void
  onFatto: (d: MedScheda) => void
}) {
  const iniziali = useMemo(() => valoriDi(d), [d])
  const [v, setV] = useState<MedCampi>(iniziali)
  const [revisione, setRevisione] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const anagrafiche = useAnagraficheMed()

  const cambiati = (Object.keys(v) as Campo[]).filter((k) => norma(v[k]) !== norma(iniziali[k]))
  const set = (k: Campo) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value })

  async function salva() {
    setErrore(null)
    setLavoro(true)
    const campi: MedCampi = {}
    for (const k of cambiati) {
      const valore = v[k]
      ;(campi as Record<string, unknown>)[k] = typeof valore === 'string' ? valore.trim() : valore
    }
    try {
      onFatto((await api.medDeviceUpdate(d.id, campi)).device)
    } catch (e) {
      setErrore(e)
      setRevisione(false)
    } finally {
      setLavoro(false)
    }
  }

  const testo = (k: Campo, opts: { span?: string; mono?: boolean; tipo?: string; max?: number; aiuto?: string } = {}) => (
    <label className={`field${opts.span ? ` ${opts.span}` : ''}`}>
      <span className="field__label">{etichetta(k)}</span>
      <input type={opts.tipo ?? 'text'} className={opts.mono ? 'mono' : undefined} value={String(v[k] ?? '')}
             onChange={set(k)} maxLength={opts.max} />
      {opts.aiuto && <span className="field__hint">{opts.aiuto}</span>}
    </label>
  )

  const anagrafica = (k: 'location_id' | 'org_unit_id' | 'cost_center_id', kind: MasterDataKind) => (
    <div className="field">
      <span className="field__label">{etichetta(k)}</span>
      <SelettoreAnagrafica kind={kind} voci={anagrafiche.tutte?.[kind] ?? []} value={v[k] as string | null}
                           onChange={(id) => setV({ ...v, [k]: id })} />
      {kind !== 'cost_center' && (
        <NuovaVoce kind={kind} onCreata={(nuova) => { anagrafiche.aggiungi(nuova); setV({ ...v, [k]: nuova.id }) }} />
      )}
    </div>
  )

  return (
    <Finestra titolo={t('Modifica la scheda')} sotto={d.name} onChiudi={onChiudi}>
      <ErrorBanner error={errore || anagrafiche.errore} />
      {revisione ? (
        <div className="form-dialog">
          <h4 className="form__sezione">{t('Cosa cambia')}</h4>
          <Revisione righe={cambiati.map((k) => ({
            campo: k, prima: leggibile(k, iniziali[k], anagrafiche.tutte), dopo: leggibile(k, v[k], anagrafiche.tutte),
          }))} />
          {cambiati.includes('model') && (
            <p className="field__hint">{t('Un modello scritto qui resta: la sincronizzazione oraria non lo cambia più. Svuotalo per lasciarlo cercare di nuovo a CENSO.')}</p>
          )}
          {cambiati.includes('status') && v.status === 'decommissioned' && (
            <p className="field__hint">{t('Un dispositivo dismesso esce dagli elenchi dell’area (resta visibile con «Anche i dismessi»).')}</p>
          )}
          <div className="form-dialog__azioni">
            <button type="button" className="secondary" onClick={() => setRevisione(false)} disabled={lavoro}>{t('Indietro')}</button>
            <button type="button" onClick={() => void salva()} disabled={lavoro}>{lavoro ? t('Salvo…') : t('Salva')}</button>
          </div>
        </div>
      ) : !anagrafiche.tutte && !anagrafiche.errore ? <Spinner /> : (
        <div className="form-dialog">
          <h4 className="form__sezione">{t('Identificazione')}</h4>
          <div className="form-grid">
            {testo('inventory_number', { mono: true, max: 64, aiuto: t('Il numero dell’Ingegneria clinica: unico nell’ospedale.') })}
            {testo('serial_number', { mono: true, max: 128 })}
            {testo('udi_di', { mono: true, max: 128 })}
            {testo('series', { max: 200 })}
            {testo('model', { max: 200, aiuto: d.model && d.model_source !== 'manual'
              ? t('Trovato da CENSO: scrivilo solo se è sbagliato.') : undefined })}
            {testo('cnd_code', { span: 'span-1', mono: true, max: 32 })}
            {testo('civab_code', { span: 'span-1', mono: true, max: 32 })}
          </div>

          <h4 className="form__sezione">{t('Dove si trova')}</h4>
          <div className="form-grid">
            {anagrafica('org_unit_id', 'org_unit')}
            {anagrafica('location_id', 'location')}
            {anagrafica('cost_center_id', 'cost_center')}
            {testo('room', { max: 120 })}
            {testo('bed', { span: 'span-1', max: 60 })}
          </div>

          <h4 className="form__sezione">{t('Stato e responsabile')}</h4>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{etichetta('status')}</span>
              <select value={v.status} onChange={(e) => setV({ ...v, status: e.target.value as MedStato })}>
                {STATI_MED.map((s) => <option key={s} value={s}>{etichettaStatoMed(s)}</option>)}
              </select>
            </label>
            {testo('status_reason', { span: 'span-4', max: 500 })}
            {testo('responsible_name', { max: 200 })}
            {testo('responsible_email', { tipo: 'email', max: 320 })}
            {testo('acceptance_date', { tipo: 'date' })}
            {testo('install_date', { tipo: 'date' })}
          </div>

          <h4 className="form__sezione">{t('Sicurezza elettrica (CEI EN 62353)')}</h4>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{etichetta('electrical_class')}</span>
              <select value={String(v.electrical_class ?? '')} onChange={set('electrical_class')}>
                <option value="">{t('non indicata')}</option>
                {classiElettriche().map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="field">
              <span className="field__label">{etichetta('applied_part')}</span>
              <select value={String(v.applied_part ?? '')} onChange={set('applied_part')}>
                <option value="">{t('non indicata')}</option>
                {partiApplicate().map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="field">
              <span className="field__label">{etichetta('patient_connected')}</span>
              <select value={v.patient_connected === null || v.patient_connected === undefined ? '' : v.patient_connected ? 'si' : 'no'}
                      onChange={(e) => setV({ ...v, patient_connected: e.target.value === '' ? null : e.target.value === 'si' })}>
                <option value="">{t('non indicato')}</option>
                <option value="si">{t('sì')}</option>
                <option value="no">{t('no')}</option>
              </select>
            </label>
          </div>

          <h4 className="form__sezione">{t('Note')}</h4>
          <textarea rows={3} value={String(v.notes ?? '')} onChange={set('notes')} maxLength={4000} aria-label={t('Note')} />

          <div className="form-dialog__azioni">
            <button type="button" className="secondary" onClick={onChiudi}>{t('Annulla')}</button>
            <button type="button" onClick={() => setRevisione(true)} disabled={cambiati.length === 0}>
              {t('Rivedi le modifiche ({n})', { n: cambiati.length })}
            </button>
          </div>
        </div>
      )}
    </Finestra>
  )
}

/** The fields a bulk change may set, and «leave as it is» for each. */
const IN_BLOCCO: Campo[] = ['org_unit_id', 'location_id', 'cost_center_id', 'status', 'status_reason',
  'responsible_name', 'responsible_email', 'electrical_class', 'applied_part', 'patient_connected']
const LOTTO = 500

export function FinestraBlocco({ ids, onChiudi, onFatto }: {
  ids: string[]
  onChiudi: () => void
  onFatto: (cambiati: number) => void
}) {
  const [scelti, setScelti] = useState<Partial<Record<Campo, boolean>>>({})
  const [v, setV] = useState<MedCampi>({ status: 'in_use', patient_connected: null })
  const [revisione, setRevisione] = useState(false)
  const [fatti, setFatti] = useState<number | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const anagrafiche = useAnagraficheMed()
  const attivi = IN_BLOCCO.filter((k) => scelti[k])

  async function applica() {
    setErrore(null)
    setFatti(0)
    const campi: MedCampi = {}
    for (const k of attivi) {
      const valore = v[k]
      ;(campi as Record<string, unknown>)[k] = typeof valore === 'string' ? valore.trim() : (valore ?? null)
    }
    let cambiati = 0
    try {
      // In batches: the WAF refuses JSON bodies with too many arguments.
      for (let i = 0; i < ids.length; i += LOTTO) {
        cambiati += (await api.medDevicesBulk(ids.slice(i, i + LOTTO), campi)).changed
        setFatti(Math.min(i + LOTTO, ids.length))
      }
      onFatto(cambiati)
    } catch (e) {
      setErrore(e)
      setRevisione(false)
      setFatti(null)
    }
  }

  const spunta = (k: Campo, controllo: ReactNode) => (
    <div className="field" key={k}>
      <span className="checkbox" style={{ marginBottom: 4 }}>
        <input id={`blocco-${k}`} type="checkbox" checked={Boolean(scelti[k])}
               onChange={(e) => setScelti({ ...scelti, [k]: e.target.checked })} />
        <label htmlFor={`blocco-${k}`}>{etichetta(k)}</label>
      </span>
      {scelti[k] && controllo}
    </div>
  )

  return (
    <Finestra titolo={t('Modifica in blocco')} sotto={t('{n} dispositivi scelti', { n: ids.length })} onChiudi={onChiudi}>
      <ErrorBanner error={errore || anagrafiche.errore} />
      {revisione ? (
        <div className="form-dialog">
          <h4 className="form__sezione">{t('Cosa cambia su {n} dispositivi', { n: ids.length })}</h4>
          <Revisione righe={attivi.map((k) => ({ campo: k, prima: t('(come ora)'), dopo: leggibile(k, v[k], anagrafiche.tutte) }))} />
          {fatti !== null && <p className="field__hint">{t('Applicati {n} di {m}…', { n: fatti, m: ids.length })}</p>}
          <div className="form-dialog__azioni">
            <button type="button" className="secondary" onClick={() => setRevisione(false)} disabled={fatti !== null}>{t('Indietro')}</button>
            <button type="button" onClick={() => void applica()} disabled={fatti !== null}>{t('Applica')}</button>
          </div>
        </div>
      ) : (
        <div className="form-dialog">
          <p className="field__hint">{t('Spunta i campi da cambiare: gli altri restano come sono su ogni dispositivo.')}</p>
          <div className="form-grid">
            {spunta('org_unit_id', (
              <>
                <SelettoreAnagrafica kind="org_unit" voci={anagrafiche.tutte?.org_unit ?? []} value={v.org_unit_id}
                                     onChange={(id) => setV({ ...v, org_unit_id: id })} />
                <NuovaVoce kind="org_unit" onCreata={(n) => { anagrafiche.aggiungi(n); setV({ ...v, org_unit_id: n.id }) }} />
              </>
            ))}
            {spunta('location_id', (
              <>
                <SelettoreAnagrafica kind="location" voci={anagrafiche.tutte?.location ?? []} value={v.location_id}
                                     onChange={(id) => setV({ ...v, location_id: id })} />
                <NuovaVoce kind="location" onCreata={(n) => { anagrafiche.aggiungi(n); setV({ ...v, location_id: n.id }) }} />
              </>
            ))}
            {spunta('cost_center_id', (
              <SelettoreAnagrafica kind="cost_center" voci={anagrafiche.tutte?.cost_center ?? []} value={v.cost_center_id}
                                   onChange={(id) => setV({ ...v, cost_center_id: id })} />
            ))}
            {spunta('status', (
              <select value={v.status} onChange={(e) => setV({ ...v, status: e.target.value as MedStato })} aria-label={etichetta('status')}>
                {STATI_MED.map((s) => <option key={s} value={s}>{etichettaStatoMed(s)}</option>)}
              </select>
            ))}
            {spunta('status_reason', (
              <input type="text" value={String(v.status_reason ?? '')} maxLength={500} aria-label={etichetta('status_reason')}
                     onChange={(e) => setV({ ...v, status_reason: e.target.value })} />
            ))}
            {spunta('responsible_name', (
              <input type="text" value={String(v.responsible_name ?? '')} maxLength={200} aria-label={etichetta('responsible_name')}
                     onChange={(e) => setV({ ...v, responsible_name: e.target.value })} />
            ))}
            {spunta('responsible_email', (
              <input type="email" value={String(v.responsible_email ?? '')} maxLength={320} aria-label={etichetta('responsible_email')}
                     onChange={(e) => setV({ ...v, responsible_email: e.target.value })} />
            ))}
            {spunta('electrical_class', (
              <select value={String(v.electrical_class ?? '')} onChange={(e) => setV({ ...v, electrical_class: e.target.value })}
                      aria-label={etichetta('electrical_class')}>
                <option value="">{t('non indicata')}</option>
                {classiElettriche().map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            ))}
            {spunta('applied_part', (
              <select value={String(v.applied_part ?? '')} onChange={(e) => setV({ ...v, applied_part: e.target.value })}
                      aria-label={etichetta('applied_part')}>
                <option value="">{t('non indicata')}</option>
                {partiApplicate().map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            ))}
            {spunta('patient_connected', (
              <select value={v.patient_connected === null || v.patient_connected === undefined ? '' : v.patient_connected ? 'si' : 'no'}
                      onChange={(e) => setV({ ...v, patient_connected: e.target.value === '' ? null : e.target.value === 'si' })}
                      aria-label={etichetta('patient_connected')}>
                <option value="">{t('non indicato')}</option>
                <option value="si">{t('sì')}</option>
                <option value="no">{t('no')}</option>
              </select>
            ))}
          </div>
          <div className="form-dialog__azioni">
            <button type="button" className="secondary" onClick={onChiudi}>{t('Annulla')}</button>
            <button type="button" onClick={() => setRevisione(true)} disabled={attivi.length === 0}>{t('Rivedi le modifiche ({n})', { n: attivi.length })}</button>
          </div>
        </div>
      )}
    </Finestra>
  )
}

export function FinestraEsclusione({ d, onChiudi, onFatto }: {
  d: MedScheda
  onChiudi: () => void
  onFatto: (d: MedScheda) => void
}) {
  const [motivo, setMotivo] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  const esci = !d.excluded

  async function conferma() {
    setErrore(null)
    setLavoro(true)
    try {
      onFatto((await api.medDeviceExclude(d.id, esci, esci ? motivo.trim() : undefined)).device)
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Finestra titolo={esci ? t('Non è un elettromedicale') : t('Rimetti fra gli elettromedicali')} sotto={d.name} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="form-dialog">
        <p>
          {esci
            ? t('Il dispositivo esce dall’area CENSO MED: dagli elenchi, dalla panoramica, dalla ricerca. La scheda resta (si rivede con «Anche gli esclusi a mano») e il CI nella CMDB non cambia.')
            : t('Il dispositivo torna negli elenchi dell’area. Il motivo dell’esclusione si cancella.')}
        </p>
        {esci && (
          <div className="form-grid">
            <label className="field span-6">
              <span className="field__label">{t('Perché non è un elettromedicale')}</span>
              <textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} />
              <span className="field__hint">{t('Per esempio: «è il PC del medico di guardia», «stampante di reparto». Resta nella scheda.')}</span>
            </label>
          </div>
        )}
        <div className="form-dialog__azioni">
          <button type="button" className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Annulla')}</button>
          <button type="button" onClick={() => void conferma()} disabled={lavoro || (esci && !motivo.trim())}>
            {esci ? t('Escludi dall’area') : t('Rimetti nell’area')}
          </button>
        </div>
      </div>
    </Finestra>
  )
}

export function FinestraModello({ chiave, onChiudi }: { chiave: string; onChiudi: () => void }) {
  const dati = useAsync(() => api.medModel(chiave), [chiave])
  const m = dati.data
  const c = m?.catalogue
  return (
    <Finestra titolo={m ? (m.model || m.series || t('Modello non indicato')) : t('Modello')} sotto={m?.vendor} onChiudi={onChiudi}>
      <ErrorBanner error={dati.error} />
      {!m ? <Spinner /> : (
        <div className="form-dialog">
          <dl className="kv">
            <dt>{t('Produttore')}</dt><dd>{m.vendor || '—'}</dd>
            <dt>{t('Serie')}</dt><dd>{m.series || '—'}</dd>
            <dt>{t('Modello')}</dt><dd>{m.model || <span className="text-muted">{t('da determinare')}</span>}</dd>
            <dt>{t('Categoria')}</dt><dd>{m.category || '—'}</dd>
            <dt>{t('Dispositivi')}</dt><dd>{m.devices} · {t('in uso {n}', { n: m.in_use })} · {t('fermi o in riparazione {n}', { n: m.out_of_service })}</dd>
            <dt>{t('Reparti')}</dt><dd>{m.wards.join(', ') || '—'}</dd>
            {c && (
              <>
                <dt>{t('Classe di rischio')}</dt><dd>{c.risk_class || '—'}</dd>
                <dt>{t('Area clinica')}</dt><dd>{c.clinical_area || '—'}</dd>
                <dt>{t('Collegato al paziente')}</dt><dd>{c.patient_connected === null ? '—' : c.patient_connected ? t('sì') : t('no')}</dd>
                <dt>{t('Fine vita')}</dt><dd>{formatDay(c.eol_date)}</dd>
                <dt>{t('Modulo MDS2')}</dt>
                <dd>{c.mds2_url ? <a href={c.mds2_url} target="_blank" rel="noreferrer noopener">{t('apri')}</a> : '—'}</dd>
              </>
            )}
          </dl>
          {m.registry && (
            <>
              <h4 className="form__sezione">{t('Registro del Ministero della Salute')}</h4>
              <dl className="kv">
                <dt>{t('Denominazione commerciale')}</dt><dd>{m.registry.name}{m.registry.verified ? '' : ` (${t('probabile')})`}</dd>
                <dt>{t('Codice catalogo')}</dt><dd className="mono">{m.registry.catalogue_code || '—'}</dd>
                <dt>{t('Classificazione CND')}</dt><dd><span className="mono">{m.registry.cnd_code}</span> · {m.registry.cnd_description}</dd>
                <dt>{t('Fuori commercio dal')}</dt><dd>{m.registry.end_of_sale ? formatDay(m.registry.end_of_sale) : t('in commercio')}</dd>
              </dl>
            </>
          )}
          {m.eudamed && (
            <>
              <h4 className="form__sezione">{t('EUDAMED — banca dati europea')}</h4>
              <dl className="kv">
                <dt>{t('Nome commerciale')}</dt><dd>{m.eudamed.name}{m.eudamed.verified ? '' : ` (${t('probabile')})`}</dd>
                <dt>{t('Riferimento del fabbricante')}</dt><dd className="mono">{m.eudamed.catalogue_code || '—'}</dd>
                <dt>{t('Classe di rischio (MDR)')}</dt><dd>{m.eudamed.risk_class || '—'}</dd>
                <dt>{t('Nomenclatura EMDN')}</dt><dd><span className="mono">{m.eudamed.emdn_code || '—'}</span>{m.eudamed.emdn_description ? ` · ${m.eudamed.emdn_description}` : ''}</dd>
              </dl>
            </>
          )}
          {m.fda && (
            <>
              <h4 className="form__sezione">{t('FDA — identificazione del dispositivo (GUDID)')}</h4>
              <dl className="kv">
                <dt>{t('Nome commerciale')}</dt><dd>{m.fda.brand_name}{m.fda.verified ? '' : ` (${t('probabile')})`}</dd>
                <dt>{t('Codice catalogo')}</dt><dd className="mono">{m.fda.catalogue_code || '—'}</dd>
                <dt>{t('Classe FDA')}</dt><dd>{m.fda.device_class || '—'}</dd>
                <dt>{t('Codice prodotto FDA')}</dt><dd><span className="mono">{m.fda.product_code || '—'}</span>{m.fda.device_name ? ` · ${m.fda.device_name}` : ''}</dd>
                <dt>{t('Sicurezza in risonanza magnetica')}</dt><dd>{m.fda.mri_safety || '—'}</dd>
              </dl>
            </>
          )}
          {m.recall_list.length > 0 && (
            <>
              <h4 className="form__sezione">{t('Richiami sul modello ({n})', { n: m.recall_list.length })}</h4>
              <DataTable
                rows={m.recall_list}
                rowKey={(r) => r.id}
                initialSort={{ key: 'posted', dir: 'desc' }}
                paginazione={m.recall_list.length > 25 ? undefined : false}
                strumenti={false}
                columns={[
                  { key: 'number', header: t('Richiamo'), mono: true, value: (r) => r.number,
                    render: (r) => (r.url ? <a href={r.url} target="_blank" rel="noreferrer noopener">{r.number}</a> : r.number) },
                  { key: 'posted', header: t('Pubblicato'), type: 'date', value: (r) => r.posted ?? '', render: (r) => formatDay(r.posted) },
                  { key: 'classification', header: t('Classe'), value: (r) => r.classification },
                  { key: 'reason', header: t('Motivo'), value: (r) => r.reason, render: (r) => <span className="med-testo-corto" title={r.reason}>{r.reason}</span> },
                  { key: 'status', header: t('Gestione'), value: (r) => r.status, render: (r) => <BadgeStatoRichiamo stato={r.status} /> },
                ]}
              />
            </>
          )}
          {!c && (
            <p className="field__hint">{m.model
              ? t('Il catalogo degli elettromedicali non ha ancora questo modello: classe di rischio, fine vita e modulo MDS2 arriveranno da lì.')
              : t('Si conosce solo la serie: i dati del catalogo valgono per un modello preciso.')}</p>
          )}
          <ProfiloModello chiave={chiave} />
          <h4 className="form__sezione">{t('I dispositivi di questo modello')}</h4>
          <DataTable
            rows={m.device_list}
            rowKey={(r) => r.id}
            initialSort={{ key: 'name' }}
            conteggio={{ nome: t('dispositivi') }}
            columns={[
              { key: 'name', header: t('Nome'), value: (r) => r.name, render: (r) => <Link to={`/medical/devices/${r.id}`}>{r.name || '—'}</Link> },
              { key: 'inventory_number', header: t('N. inventario'), mono: true, value: (r) => r.inventory_number ?? '' },
              { key: 'status', header: t('Stato'), value: (r) => etichettaStatoMed(r.status), render: (r) => <BadgeStatoMed stato={r.status} /> },
              { key: 'org_unit', header: t('Reparto'), value: (r) => r.org_unit?.name ?? '' },
              { key: 'ip', header: t('Indirizzo IP'), mono: true, value: (r) => r.ip ?? '' },
            ]}
          />
        </div>
      )}
    </Finestra>
  )
}
